import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { FileUploader } from "react-drag-drop-files";
import { PrimaryButton } from "../../elements/buttons";
import useUploadMedia from "../../../hooks/useUploadMedia";
import useCreateUpdateBanner from "../../../hooks/banners/useCreateUpdateBanner";
import useGetBanner from "../../../hooks/banners/useGetBanner";
import Spinner from "../../spinner";
import { toast } from "react-hot-toast";

const fileTypes = ["JPG", "PNG", "JPEG", "WEBP"];

const AddEditBannerForm = ({ isEdit = false }) => {
  const { bannerId } = useParams();
  const navigate = useNavigate();

  const {
    control,
    setValue,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      media_id: "",
      banner_url: "",
      image_alt: "",
      mobile_media_id: "",
      mobile_image_alt: "",
    },
  });

  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [imageId, setImageId] = useState("");
  const [imageError, setImageError] = useState("");

  // Mobile banner states
  const [mobileImageUploading, setMobileImageUploading] = useState(false);
  const [mobileImagePreview, setMobileImagePreview] = useState("");
  const [mobileImageId, setMobileImageId] = useState("");
  const [mobileImageError, setMobileImageError] = useState("");

  // For editing, fetch banner details
  useGetBanner({
    bannerId: isEdit ? bannerId : null,
    onSuccess: (data) => {
      if (isEdit) {
        const resData = data?.data || {};
        setValue("name", resData.name || "");
        setValue("banner_url", resData.banner_url || "");
        setValue("image_alt", resData.image_alt || "");
        setValue("mobile_image_alt", resData.mobile_image_alt || "");
        if (resData.media_id) {
          setImageId(resData.media_id);
          setImagePreview(resData.media_path || "");
          setValue("media_id", resData.media_id);
        }
        if (resData.mobile_media_id) {
          setMobileImageId(resData.mobile_media_id);
          setMobileImagePreview(resData.mobile_media_path || "");
          setValue("mobile_media_id", resData.mobile_media_id);
        }
      }
    },
  });

  // Image upload handlers (desktop)
  const handleMediaUploadSuccess = (data) => {
    setImageId(data.data.image_id);
    setImagePreview(data.data.image_url);
    setValue("media_id", data.data.image_id);
    setImageUploading(false);
    toast.success("Image uploaded successfully", {
      position: "top-right",
      duration: 2000,
    });
  };

  const handleMediaUploadError = (error) => {
    setImageUploading(false);
    toast.error(error?.response?.data?.message || "Error uploading image", {
      position: "top-right",
      duration: 3000,
    });
  };

  // Image upload handlers (mobile)
  const handleMobileMediaUploadSuccess = (data) => {
    setMobileImageId(data.data.image_id);
    setMobileImagePreview(data.data.image_url);
    setValue("mobile_media_id", data.data.image_id);
    setMobileImageUploading(false);
    toast.success("Mobile image uploaded successfully", {
      position: "top-right",
      duration: 2000,
    });
  };

  const handleMobileMediaUploadError = (error) => {
    setMobileImageUploading(false);
    toast.error(error?.response?.data?.message || "Error uploading mobile image", {
      position: "top-right",
      duration: 3000,
    });
  };

  const { upload } = useUploadMedia({
    url: "upload/image",
    onSuccess: handleMediaUploadSuccess,
    onError: handleMediaUploadError,
  });

  const { upload: uploadMobile } = useUploadMedia({
    url: "upload/image",
    onSuccess: handleMobileMediaUploadSuccess,
    onError: handleMobileMediaUploadError,
  });

  const handleImageUpload = (file) => {
    setImageUploading(true);
    upload.mutate({ thumbnail: file, screen: "BANNER" });
  };

  const handleMobileImageUpload = (file) => {
    setMobileImageUploading(true);
    uploadMobile.mutate({ thumbnail: file, screen: "BANNER" });
  };

  const handleReUpload = () => {
    setImagePreview("");
    setImageId("");
    setValue("media_id", "");
  };

  const handleMobileReUpload = () => {
    setMobileImagePreview("");
    setMobileImageId("");
    setValue("mobile_media_id", "");
  };

  const { handleSubmit: handleCreateUpdate } = useCreateUpdateBanner({
    reset: () => reset(),
  });

  const onSubmit = (formData) => {
    if (!imageId) {
      setImageError("Banner image is required");
      return;
    }
    setImageError("");
    if (!mobileImageId) {
      setMobileImageError("Mobile banner image is required");
      return;
    }
    setMobileImageError("");
    const payload = {
      banner_id: isEdit ? bannerId : "",
      name: formData.name,
      media_id: imageId,
      banner_url: formData.banner_url,
      image_alt: formData.image_alt,
      mobile_media_id: mobileImageId,
      mobile_image_alt: formData.mobile_image_alt,
    };
    handleCreateUpdate(payload);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="py-4">
      <div className="mb-6">
        <Controller
          name="name"
          control={control}
          rules={{
            required: "Banner Name is required",
          }}
          render={({ field: { value, onChange } }) => (
            <div>
              <label htmlFor="Banner Name" className="label">
                Banner Name <small className="text-red-700">*</small>
              </label>
              <input
                required
                type="text"
                placeholder="Enter Banner Name"
                name="name"
                className="input"
                value={value}
                onChange={onChange}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.name.message}
                </p>
              )}
            </div>
          )}
        />
      </div>
      <div className="mb-6">
        <Controller
          name="banner_url"
          control={control}
          rules={{
            required: false,
          }}
          render={({ field: { value, onChange } }) => (
            <div>
              <label htmlFor="Banner URL" className="label">
                Banner URL
              </label>
              <input
                required
                type="url"
                placeholder="Enter Banner URL"
                name="banner_url"
                className="input"
                value={value}
                onChange={onChange}
              />
              {errors.banner_url && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.banner_url.message}
                </p>
              )}
            </div>
          )}
        />
      </div>

      {/* Desktop Banner Image Upload */}
      <div className="mb-6">
        <label htmlFor="Banner Image" className="label">
          Desktop Banner Image <span className="text-red-700">*</span> <small className="text-red-700">(Recommended size: 3840x1579)</small>
        </label>
        <div className="w-full mt-2 flex flex-col items-center">
          {imageUploading ? (
            <div className="flex flex-col items-center justify-center h-64 w-full">
              <Spinner />
              <p className="mt-2">Uploading image...</p>
            </div>
          ) : !imagePreview ? (
            <FileUploader
              name="banner_image"
              types={fileTypes}
              handleChange={handleImageUpload}
              classes="file-uploader"
              hoverTitle="Drop here"
            />
          ) : (
            <div className="border border-dashed border-blue-700 rounded flex flex-col items-center justify-center w-full p-2">
              <div
                className="relative w-full flex items-center justify-center"
                style={{ minHeight: "240px" }}
              >
                <img
                  src={imagePreview}
                  alt="banner"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "400px",
                    objectFit: "contain",
                    width: "auto",
                    height: "auto",
                    display: "block",
                  }}
                  className="rounded bg-white shadow"
                />
              </div>
              <div className="w-full mt-2 flex flex-col items-center">
                <button
                  type="button"
                  className="mt-2 text-blue-600 underline text-xs"
                  onClick={handleReUpload}
                >
                  Re-Upload
                </button>
              </div>
            </div>
          )}
          {imageError && (
            <p className="text-red-500 text-xs mt-2">{imageError}</p>
          )}
        </div>
      </div>
      {/* Desktop Banner Alt Text */}
      <div className="mb-6">
        <Controller
          name="image_alt"
          control={control}
          rules={{
            required: false,
          }}
          render={({ field: { value, onChange } }) => (
            <div>
              <label htmlFor="image_alt" className="label">
                Desktop Banner Image Alt Text
              </label>
              <input
                type="text"
                placeholder="Enter alt text for desktop banner"
                name="image_alt"
                className="input"
                value={value}
                onChange={onChange}
              />
              {errors.image_alt && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.image_alt.message}
                </p>
              )}
            </div>
          )}
        />
      </div>

      {/* Mobile Banner Image Upload */}
      <div className="mb-6">
        <label htmlFor="Mobile Banner Image" className="label">
          Mobile Banner Image <span className="text-red-700">*</span> <small className="text-red-700">(Recommended size: 1280x526)</small>
        </label>
        <div className="w-full mt-2 flex flex-col items-center">
          {mobileImageUploading ? (
            <div className="flex flex-col items-center justify-center h-64 w-full">
              <Spinner />
              <p className="mt-2">Uploading image...</p>
            </div>
          ) : !mobileImagePreview ? (
            <FileUploader
              name="mobile_banner_image"
              types={fileTypes}
              handleChange={handleMobileImageUpload}
              classes="file-uploader"
              hoverTitle="Drop here"
            />
          ) : (
            <div className="border border-dashed border-blue-700 rounded flex flex-col items-center justify-center w-full p-2">
              <div
                className="relative w-full flex items-center justify-center"
                style={{ minHeight: "240px" }}
              >
                <img
                  src={mobileImagePreview}
                  alt="mobile banner"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "400px",
                    objectFit: "contain",
                    width: "auto",
                    height: "auto",
                    display: "block",
                  }}
                  className="rounded bg-white shadow"
                />
              </div>
              <div className="w-full mt-2 flex flex-col items-center">
                <button
                  type="button"
                  className="mt-2 text-blue-600 underline text-xs"
                  onClick={handleMobileReUpload}
                >
                  Re-Upload
                </button>
              </div>
            </div>
          )}
          {mobileImageError && (
            <p className="text-red-500 text-xs mt-2">{mobileImageError}</p>
          )}
        </div>
      </div>
      {/* Mobile Banner Alt Text */}
      <div className="mb-6">
        <Controller
          name="mobile_image_alt"
          control={control}
          rules={{
            required: false,
          }}
          render={({ field: { value, onChange } }) => (
            <div>
              <label htmlFor="mobile_image_alt" className="label">
                Mobile Banner Image Alt Text
              </label>
              <input
                type="text"
                placeholder="Enter alt text for mobile banner"
                name="mobile_image_alt"
                className="input"
                value={value}
                onChange={onChange}
              />
              {errors.mobile_image_alt && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.mobile_image_alt.message}
                </p>
              )}
            </div>
          )}
        />
      </div>

      <div className="flex justify-end mt-8">
        <PrimaryButton label={isEdit ? "Update" : "Save"} type={"submit"} />
      </div>
    </form>
  );
};

export default AddEditBannerForm;
